import { useState } from "react";
import type { SessionUser } from "../../../shared/types";
import { Ico } from "../../components/shell";
import { AtlShell } from "../../components/atelier";
import { useAuth } from "../../auth";
import { api, ApiError } from "../../api";
import { navigate } from "../../router";

function initialsOf(name: string): string {
  const w = name.trim().split(/\s+/).filter(Boolean);
  if (w.length >= 2) return (w[0][0] + w[1][0]).toUpperCase();
  return name.trim().slice(0, 2).toUpperCase() || "??";
}

function roleLabel(role: SessionUser["role"]): string {
  return role === "super_admin" ? "Super admin" : role === "admin" ? "Admin" : "Host";
}

export default function Account() {
  const { user, logout } = useAuth();
  if (!user) return null;

  return (
    <AtlShell
      active="Account"
      title="Account"
      sub={`SIGNED IN AS ${user.username.toUpperCase()}`}
      actions={
        <button
          className="atl-btn atl-btn--ghost"
          onClick={async () => {
            await logout();
            navigate("/login");
          }}
        >
          <Ico name="export" size={15} /> Sign out
        </button>
      }
    >
      <div style={{ padding: 30, display: "flex", flexDirection: "column", gap: 18, maxWidth: 720 }}>
        <ProfileCard user={user} />
        <PasswordCard />
      </div>
    </AtlShell>
  );
}

function ProfileCard({ user }: { user: SessionUser }) {
  return (
    <div className="atl-card" style={{ padding: 24, display: "flex", alignItems: "center", gap: 18 }}>
      <span
        style={{
          width: 66,
          height: 66,
          borderRadius: 18,
          background: "var(--atl-accent)",
          color: "#fff",
          display: "grid",
          placeItems: "center",
          fontFamily: "var(--atl-serif)",
          fontWeight: 600,
          fontSize: 26,
          flex: "0 0 auto",
        }}
      >
        {initialsOf(user.displayName)}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="atl-serif" style={{ fontSize: 22 }}>{user.displayName}</div>
        <div style={{ fontSize: 13.5, color: "var(--atl-faint)", fontWeight: 500, marginTop: 2 }}>
          @{user.username} · {roleLabel(user.role)}
        </div>
      </div>
      <span className="atl-chip atl-chip--accent">
        {roleLabel(user.role)}
      </span>
    </div>
  );
}

function PasswordCard() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.changePassword(cur, next);
      setMsg({ ok: true, text: "Password updated 🎉" });
      setCur("");
      setNext("");
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : "Could not update" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="atl-card" style={{ padding: 24 }}>
      <h3 className="atl-serif" style={{ fontSize: 19 }}>Change password</h3>
      <p style={{ fontSize: 13.5, color: "var(--atl-soft)", fontWeight: 500, marginTop: 4, marginBottom: 18 }}>
        Keep your bakery account safe and sound.
      </p>
      <div className="atl-respond" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, maxWidth: 480 }}>
        <div>
          <div className="atl-eyebrow" style={{ marginBottom: 8 }}>CURRENT PASSWORD</div>
          <input className="atl-tf" type="password" value={cur} onChange={(e) => setCur(e.target.value)} />
        </div>
        <div>
          <div className="atl-eyebrow" style={{ marginBottom: 8 }}>NEW PASSWORD</div>
          <input className="atl-tf" type="password" placeholder="At least 6 characters" value={next} onChange={(e) => setNext(e.target.value)} />
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 18 }}>
        <button className="atl-btn atl-btn--primary" disabled={busy || cur.length < 1 || next.length < 6} onClick={save}>
          Update password
        </button>
        {msg && (
          <span
            className="atl-chip"
            style={{
              fontWeight: 600,
              borderColor: msg.ok ? "#cfe0c2" : "#e2c5ca",
              color: msg.ok ? "#5d7344" : "var(--atl-accent)",
              background: msg.ok ? "#f1f5ea" : "#fbf2f3",
            }}
          >
            {msg.text}
          </span>
        )}
      </div>
    </div>
  );
}
